import { AppConfig, ProjectProjection } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

class APIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        const error = data as ApiError;
        throw new Error(error.error.message || 'Request failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // Authentication
  async register(email: string, password: string) {
    return this.request<{ user: { id: string; email: string }; message: string }>(
      '/api/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    );
  }

  async login(email: string, password: string) {
    return this.request<{ user: { id: string; email: string }; token: string }>(
      '/api/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    );
  }

  async logout() {
    return this.request<{ message: string }>('/api/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request<{ user: { id: string; email: string } }>('/api/auth/me');
  }

  // Configuration
  async getConfig() {
    return this.request<{ config: AppConfig }>('/api/config');
  }

  async updateConfig(updates: Partial<AppConfig>) {
    return this.request<{ config: AppConfig }>('/api/config', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async updateCustomRates(
    salaryType: 'ordinario_inf' | 'ordinario_sup' | 'integral',
    rates: Record<string, number>
  ) {
    return this.request<{ config: AppConfig }>('/api/config/custom-rates', {
      method: 'PUT',
      body: JSON.stringify({ salaryType, rates }),
    });
  }

  async resetConfig() {
    return this.request<{ config: AppConfig }>('/api/config/reset', {
      method: 'POST',
    });
  }

  // Projects
  async listProjects() {
    return this.request<{ projects: any[] }>('/api/projects');
  }

  async createProject(name: string) {
    return this.request<{ project: any }>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getProject(id: string) {
    return this.request<{ project: any }>(`/api/projects/${id}`);
  }

  async updateProject(id: string, updates: { name?: string; projection?: ProjectProjection }) {
    return this.request<{ project: any }>(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string) {
    return this.request<{ message: string }>(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async addResource(projectId: string, resourceData: any) {
    return this.request<{ resource: any }>(`/api/projects/${projectId}/resources`, {
      method: 'POST',
      body: JSON.stringify(resourceData),
    });
  }

  async removeResource(projectId: string, resourceId: string) {
    return this.request<{ message: string }>(
      `/api/projects/${projectId}/resources/${resourceId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Migration
  async migrateLocalStorage(data: any) {
    return this.request<{ message: string; result: any }>('/api/migrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const apiClient = new APIClient();
